import React, { memo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { useClassName, useWindowSize } from 'common/hooks';
import { ControlledInputContainer } from 'public/components/ControlledInputContainer';
import { SVGIcon } from 'common/components';
import { BottomSheet } from 'public/components/BottomSheet';
import { Dropdown } from 'public/components/Dropdown';
import { getOptionLabel, toOption } from 'common/helpers';

import Checkbox from './Checkbox';

import './multiselect.less';

const MultiSelect = ({
  items,
  selectedItems,
  setSelectedItems,
  error,
  onBlur,
  placeholder,
  title,
}) => {
  const [open, setOpen] = useState(false);
  const classNames = useClassName('Multiselect');
  const { isMobile } = useWindowSize();

  const onClose = useCallback(() => {
    onBlur({ value: selectedItems });
    setOpen(false);
  }, [selectedItems]);

  const options = (items || []).map(toOption);

  const content = (
    <div className={classNames('list')}>
      <Checkbox
        label="All"
        key="All"
        className={classNames('item')}
        checked={selectedItems?.length === options.length}
        onChange={() => {
          setSelectedItems(
            selectedItems?.length === options.length
              ? []
              : options.map(({ value }) => value)
          );
        }}
      />
      {options.map(({ value, label }) => (
        <Checkbox
          label={label}
          key={value}
          className={classNames('item')}
          checked={selectedItems?.includes(value)}
          onChange={() => {
            setSelectedItems(
              selectedItems?.includes(value)
                ? selectedItems.filter((a) => a !== value)
                : [...selectedItems, value]
            );
          }}
        />
      ))}
    </div>
  );

  return (
    <ControlledInputContainer
      input={
        <input
          value={
            selectedItems
              ?.map((value) => getOptionLabel(options, value))
              .join(', ') || ''
          }
          readOnly
          placeholder={placeholder}
        />
      }
      addonRight={
        <SVGIcon name="angle-down" className={classNames('addon-right')} />
      }
      onClick={() => setOpen(true)}
      hasFocus={open}
      error={error}
      trigger={
        isMobile ? (
          <BottomSheet
            onClose={onClose}
            title={title}
            isOpen={open}
            content={content}
          />
        ) : (
          <Dropdown
            isOpen={open}
            onClose={onClose}
            content={content}
            useTriggerWidth
          />
        )
      }
    />
  );
};

MultiSelect.propTypes = {
  items: PropTypes.array,
  selectedItems: PropTypes.array,
  setSelectedItem: PropTypes.func,
  error: PropTypes.string,
  placeholder: PropTypes.string,
  onBlur: PropTypes.func,
  title: PropTypes.string,
};

export default memo(MultiSelect);
